/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import MsDyn365, { getCatalogId, IComponent, IComponentProps, IGridSettings, IImageSettings, msdyn365Commerce, TelemetryEvent } from '@msdyn365-commerce/core';
import { getCartState, ICartActionResult, ICartState } from '@msdyn365-commerce/global-state';
import {
    CartLineValidationResults, CartsDataActions, CommerceProperty, Customer, format,
    ProductAvailableQuantity, ProductDimension, ProductPrice,
    ProductsDataActions, ProductSearchCriteria, ProductType, SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { ErrorNotification, NotificationsManager } from '@msdyn365-commerce-modules/notifications-core';
import { ArrayExtensions, generateProductImageUrl, ObjectExtensions, IProductInventoryInformation, DeliveryMode } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { Popup, IPopupProps } from '../../../themes/adventureworks/views/components/Popup';
import classnames from 'classnames';
import React, { useState } from 'react';
import { IAlternateItems } from './buybox-components';
import {//AllowBackorder,
    IAlternateProduct} from '../../../themes/adventureworks/views/common/buyboxInterface';
import {
    IItemsAddedToCartResources,
    ItemsAddedToCartDialogComponent,
    ItemSuccessfullyAddedToCartNotification,
    MultiItemsSuccessfullyAddedToCartNotification,
    OrderDetailsProduct
} from '@msdyn365-commerce/components';
import { PriceComponent } from '../../../themes/adventureworks/views/components/price.components';
import { ConfirmAddedToCartDialog } from './add-to-cart-confirmation-dialog-component';
/**
 * Interface for add to cart resources.
 */
export interface IAddToCartResources {
    goToCartText: string;
    continueShoppingText: string;
    closeNotificationLabel: string;
    headerItemOneText: string;
    headerItemFormatText: string;
    headerLinesFormatText?: string;
    headerMessageText: string;
    freePriceText: string;
    originalPriceText: string;
    currentPriceText: string;
    addedQuantityText: string;
    buyAgainNotificationTitle?: string;
    buyAgainNotificationCloseAriaLabel?: string;
    validationErrorMessage?: string;
    oneErrorText?: string;
    multiErrorsText?: string;
}

/**
 * Interface for add to cart components props.
 */
export interface IAddToCartComponentProps extends IComponentProps<{ product: SimpleProduct; price?: ProductPrice } | undefined> {
    className?: string;
    addToCartText: string;
    outOfStockText?: string;
    disabled?: boolean;
    quantity?: number;
    navigationUrl?: string;
    productAvailability?: ProductAvailableQuantity;
    getSelectedProduct?: Promise<SimpleProduct | null>;
    isNavigationToCartPageDisabled?: boolean;
    shouldSkipSiteSettings?: boolean;
    addToCartArialLabel?: string;
    products?: {
        productId: number;
        quantity: number;
        catalogId?: number;
    }[];
    orderDetailsProducts?: OrderDetailsProduct[];
    hasAvailableProducts?: boolean;
    imageSettings?: IImageSettings;
    gridSettings?: IGridSettings;
    confirmHeaderText?: string;
    confirmBodyText?: string;
    alternateItemHeaderText?: string;
    alternateItemBodyText?: string;
    confirmStateHeaderText?: string;
    confirmStateBodyText?: string;
    isLoading?: boolean;
    isItemSupersed: boolean;
    isItemSoftSupersed: boolean;
    isLimitedBackOrdered: boolean;
    isUpdatingDimension?: boolean;
    isLoadingDeliveryOptions?: boolean;
    isUpdatingDeliveryOptions?: boolean;
    isAddServiceItemToCart?: boolean;
    isAddEmailDeliveryItemToCart?: boolean;
    isPriceKeyedIn?: boolean;
    customPriceAmount?: number;
    isOrderQuantityLimitsFeatureEnabled?: boolean;
    isBackOrdered: boolean;
    dialogStrings?: IAddToCartResources;
    softAlternateItemPopUpHeader?: string;
    softAlternateItemPopUpBody?: string;
    limitedBackorderedHeader?: string;
    limitedBackorderedBody?: string;
    telemetryContent?: ITelemetryContent;

    catalogId?: number;

    isCustomPriceSelected?: boolean;
    maximumKeyInPrice?: number;
    minimumKeyInPrice?: number;
    defaultMaximumKeyInPrice?: number;
    defaultMinimumKeyInPrice?: number;
    isProductQuantityLoading?: boolean;

    isAlternateItemAvailable: boolean;

    onAdd?(result: ICartActionResult, allowClose?: boolean): void;
    onQuickViewClosePopup(allowClose?: boolean): void;
    onError?(result: IAddToCartCustomFailureResult): void;
    changeUpdatingDimension?(isUpdatingDimension: boolean): void;
    changeUpdatingDeliveryOptions?(isUpdatingDeliveryOptions: boolean): void;
    changeConfirmPopupMessage(confirmHeaderText: string, confirmBodyText: string, isItemSupersede: boolean): void;
    updateSoftSupresedeFlag(isItemSoftSupresede: boolean): void;
    updateLimitedBackordered(isLimitedBackordered: boolean): void;
    updateBackOrdered(isBackOrdered: boolean): void;
    alternateItem?: IAlternateItems;
    inventoryLabel?: string;
    alternateProduct?: IAlternateProduct;
    customerInfo?: Customer;
    isOversizedItem?: boolean;
    isAlternateOversized?: boolean;
    cartState:ICartState;
    alternateProductPrice?: ProductPrice[];
    isPriceMinMaxEnabled?: boolean;
    priceRangeSeparator?: string;
}

/**
 * Cart action Failure reason type.
 */
export declare type ICartActionFailureReason = 'EMPTYINPUT' | 'MISSINGDIMENSION' | 'OUTOFSTOCK' | 'CARTACTIONFAILED' | 'INVALIDCUSTOMAMOUNT' | 'INVALIDQUANTITY' |'CUSTOMQUNTITYLIMIT';

/**
 * Interface for add to cart failure result.
 */
export interface IAddToCartCustomFailureResult {
    failureReason: ICartActionFailureReason;
    alternateItem?: IAlternateItems;
    alternateProduct?: IAlternateProduct;
    stockLeft?: number;
    cartActionResult?: ICartActionResult;
    missingDimensions?: ProductDimension[];
}

/**
 * This setting defines the experience when a product is added to cart. Corresponds to the configuration in Fabrikam.
 */
export enum AddToCartBehavior {

    /**
     * Navigate to cart page.
     */
    goToCart = 'goToCart',

    /**
     * Show item added to cart popup.
     */
    showModal = 'showModal',

    /**
     * Show mini cart popup.
     */
    showMiniCart = 'showMiniCart',

    /**
     * Show item added to cart notification.
     */
    showNotification = 'showNotification',

    /**
     * Do nothing and stay on the page.
     */
    nothing = 'nothing'
}

const zero = 0;
const defaultQuantity = 1;

/**
 * The propagate result.
 * @param props -- The add to cart component props.
 * @param result -- The cart action result.
 */
const propagateResult = (props: IAddToCartComponentProps, result: ICartActionResult, isClose: boolean): void => {
    if (props.onAdd) {
        props.onAdd(result, isClose);
    }
};

const updateConfirmMsg = (props: IAddToCartComponentProps, headerText: string, bdyText: string, isItemSupersede: boolean): void => {
    props.changeConfirmPopupMessage(headerText, bdyText, isItemSupersede);
};

const updateSoftSupresedeFlag = (props: IAddToCartComponentProps, isItemSoftSupersede: boolean): void => {
    props.updateSoftSupresedeFlag(isItemSoftSupersede);
};

// const updateLimitedBackOrder = (props: IAddToCartComponentProps, isLimitedBackordered: boolean): void => {
//     props.updateLimitedBackordered(isLimitedBackordered);
// };

// const updateBackorderedFlag = (props: IAddToCartComponentProps, isItemSoftSupersedeYes: boolean): void => {
//     props.updateBackOrdered(isItemSoftSupersedeYes);
// };

/**
 * The propagate error.
 * @param props -- The add to cart component props.
 * @param result -- The add to cart failure result.
 */
const propagateError = (props: IAddToCartComponentProps, result: IAddToCartCustomFailureResult): void => {
    if (props.onError) {
        props.onError(result);
    }
};

/**
 * Retrieves add to cart input from the component props.
 * @param props - Add to cart component props.
 * @returns Add to cart input.
 */
const getAddToCartInputFromProps = async (props: IAddToCartComponentProps) => {
    const products = props.orderDetailsProducts?.filter(product => !ObjectExtensions.isNullOrUndefined(product.simpleProduct));
    const input = products?.map(product => {
        return {
            product: product.simpleProduct!,
            count: ObjectExtensions.isNullOrUndefined(product.salesLine.Quantity) ? defaultQuantity : product.salesLine.Quantity,
            catalogId: product.salesLine.CatalogId
        };
    });

    if (ArrayExtensions.hasElements(input)) {
        return input;
    }

    if (ArrayExtensions.hasElements(props.products)) {
        const actionContext = props.context.actionContext;
        const apiSettings = actionContext.requestContext.apiSettings;

        const searchCriteriaInput: ProductSearchCriteria = {
            Context: {
                ChannelId: apiSettings.channelId,
                CatalogId: props.catalogId ?? getCatalogId(props.context.actionContext.requestContext)
            },
            IncludeAttributes: false,
            Ids: props.products.map(product => product.productId)
        };

        const searchResult = await ProductsDataActions.searchByCriteriaAsync(
            { callerContext: actionContext }, searchCriteriaInput);
        const productSearchResultsWithImages = searchResult.map(productSearchResult => {
            const newProductSearchResult = { ...productSearchResult };
            const newImageUrl = generateProductImageUrl(newProductSearchResult, apiSettings);

            if (newImageUrl) {
                newProductSearchResult.PrimaryImageUrl = newImageUrl;
            }

            return newProductSearchResult;
        });

        return props.products.map(item => {
            const productData = productSearchResultsWithImages.find(product => product.RecordId === item.productId);
            let simpleProduct: SimpleProduct;
            if (productData) {
                simpleProduct = {
                    ...productData,
                    ProductTypeValue: ProductType.Variant,
                    AdjustedPrice: productData.Price,
                    BasePrice: productData.BasePrice ?? productData.Price
                };
            } else {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Cast to simple product since only record id is available.
                simpleProduct = {
                    RecordId: item.productId
                } as SimpleProduct;
            }

            return {
                product: simpleProduct,
                count: item.quantity,
                catalogId: item.catalogId
            };
        });
    }

    if (props.data?.product) {
        return [
            {
                product: props.data.product,
                count: props.quantity ?? defaultQuantity,
                catalogId: props.catalogId
            }
        ];
    }

    return [];
};

/**
 * Add one item to cart when it is not buy again.
 * @param props -- The props.
 * @param setDisabled -- The set disable call back.
 * @param openModal -- The open modal call back.
 */
const addOneItemToCart = async (props: IAddToCartComponentProps,
    setDisabled: (disabled: boolean) => void,
    openModal: (opened: boolean) => void,
    setConfirmAddedToCartDialogOpen: (open: boolean) => void,
    alternateItem: IAlternateItems | undefined, alternateProduct?: IAlternateProduct, isSoftSupresedeNo?: boolean, gcBackOrderAllow?:string): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- read config file.
    const addToCartBehavior = props.context.app.config.addToCartBehavior;
    const data = props.data;
    const addToCartInput = await getAddToCartInputFromProps(props);
    let product = addToCartInput[0].product;
    const quantity = addToCartInput[0].count;

    const cartState = await getCartState(props.context.actionContext);
    // let existqty = cartState.cart.CartLines?.filter(x => x.ItemId === product.ItemId);
    // let qty = 0;
    // if (existqty) {
    //     existqty.forEach(function (item, index) {
    //         qty += item.Quantity!;
    //     });

    // }
    let limitBackorder: boolean = false, backOrder: boolean = false, softSupresed: boolean = false;
    if (alternateItem && alternateProduct && alternateItem.UseAltItemId === 'InventWhen0' && props.inventoryLabel && props.inventoryLabel?.toLowerCase().search('out of stock') > -1 && !isSoftSupresedeNo) {
        softSupresed = true;
        updateSoftSupresedeFlag(props, true);
    }
    // if (props.inventoryLabel && props.inventoryLabel?.toLowerCase().search('out of stock') > -1 && (!alternateItem || !alternateProduct || (alternateItem && alternateItem.UseAltItemId !== 'Always'))) {
    //     backOrder = true;

    //     updateBackorderedFlag(props, true);

    // }
    // else if (props.inventoryLabel && props.inventoryLabel?.toLowerCase().search('out of stock') === -1 && (!alternateItem || !alternateProduct || (alternateItem && alternateItem.UseAltItemId !== 'Always')) && ((props.productAvailability && quantity > (props.productAvailability?.AvailableQuantity ?? 0)) || (quantity + qty > props?.productAvailability?.AvailableQuantity! ?? 0 ))) {
    //     limitBackorder = true;
    //     updateLimitedBackOrder(props, true);

    // }

    if (softSupresed || backOrder || limitBackorder) {
        let header: string = '', body: string = '';
        // if((backOrder || limitBackorder) && gcBackOrderAllow === AllowBackorder.Yes){
        //     _addItemToCartCustom(props, setDisabled, openModal, setConfirmAddedToCartDialogOpen, quantity + qty);
        //     return;
        // }
        if(softSupresed)//&& (backOrder || limitBackorder))
        {
            header = 'Confirm Supersede'; //'or Backorder option';
            //body = format(`Part Number {0} has been discontinued and superseded to {1}.<ul><li>Click 'Continue' to add part number {1} to cart.</li><li>Click 'Backorder' to add part number {0} to cart.</li><li>Click 'Cancel' to cancel the operation.</li></ul>`,product.ItemId,alternateProduct?.productDtl?.ItemId);
            body = format(`Part Number {0} has been discontinued and superseded to {1}.<ul><li>Click 'Continue' to add part number {1} to cart.</li><li>Click 'Cancel' to cancel the operation.</li></ul>`,product.ItemId,alternateProduct?.productDtl?.ItemId);
        }
        // else if (softSupresed)
        // {
        //     header = props.softAlternateItemPopUpHeader??'';
        //     body = format(props.softAlternateItemPopUpBody??'',alternateProduct!.productDtl!.ItemId);
        // }
        // else if(backOrder) {
        //     if (header && header.length > 0) {
        //         header = `${header} and ${props.confirmHeaderText}` ?? '';
        //         body = `${body} and ${props.confirmBodyText}` ?? '';
        //     }
        //     else {
        //         header = props.confirmHeaderText ?? '';
        //         body = props.confirmBodyText ?? '';
        //     }
        // }
        // else if (limitBackorder) {
        //     let limitmsg: string = '';// qty = 5, ava = 8
        //     if (props.productAvailability?.AvailableQuantity && props.productAvailability.AvailableQuantity < qty) {
        //         limitmsg = format(props.limitedBackorderedBody ?? "", quantity  );
        //     }
        //     else {
        //         limitmsg = format(props.limitedBackorderedBody ?? "", (quantity + qty) - (props.productAvailability?.AvailableQuantity ?? 0));
        //     }


        //     if (header && header.length > 0) {
        //         header = `${header} and ${props.limitedBackorderedHeader}` ?? '';
        //         body = `${body} and ${limitmsg}`;
        //     }
        //     else {
        //         header = props.limitedBackorderedHeader ?? '';
        //         body = limitmsg;
        //     }
        // }

        setConfirmAddedToCartDialogOpen(true);
        updateConfirmMsg(props, header, body, false);
    }
    else {
        let productAvailableQuantity = props.productAvailability && props.productAvailability.AvailableQuantity;
        //let isAlternateProduct=false,isAlternateItemOOS=false;
        if (alternateItem && alternateItem.UseAltItemId === 'Always' && alternateProduct) {
            product = alternateProduct.productDtl!;
            productAvailableQuantity = alternateProduct.productAvailibility && alternateProduct.productAvailibility[0] && alternateProduct.productAvailibility[0].ProductAvailableQuantity && alternateProduct.productAvailibility[0].ProductAvailableQuantity.AvailableQuantity ? alternateProduct.productAvailibility[0].ProductAvailableQuantity.AvailableQuantity : undefined;
            // existqty = cartState.cart.CartLines?.filter(x => x.ItemId === product.ItemId);
            //  qty = 0;
            //  isAlternateProduct = true;
            //  isAlternateItemOOS= alternateProduct && alternateProduct.productAvailibility ? _getInventoryLabel(alternateProduct.productAvailibility)!.toLowerCase().search('out of stock') > -1 ? true : false  : false;
            // if (existqty) {
            // existqty.forEach(function (item, index) {
            //     qty += item.Quantity!;
            // });

            // }
        }
        //if(!isAlternateProduct || (isAlternateProduct && (!isAlternateItemOOS || gcBackOrderAllow === AllowBackorder.Yes)))
        // {

            const addToCartResult = await cartState.addProductToCart({
                product,
                count: quantity,
                availableQuantity: productAvailableQuantity, //&& (props.productAvailability?.AvailableQuantity === 0 || props.productAvailability?.AvailableQuantity < quantity || props.productAvailability?.AvailableQuantity < quantity + qty) ? quantity + qty : props.productAvailability?.AvailableQuantity,
                additionalProperties: { orderQuantityLimitsFeatureIsEnabled: props.isOrderQuantityLimitsFeatureEnabled },
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- read config file.
                enableStockCheck: props.context.app.config.enableStockCheck,
                isPriceKeyedIn: props.isPriceKeyedIn,
                customPrice: props.customPriceAmount,
                isAddEmailDeliveryItemToCart: props.isAddEmailDeliveryItemToCart,
                shouldSkipSiteSettings: props.shouldSkipSiteSettings,
                catalogId: addToCartInput[0].catalogId
            });
            if (addToCartResult.status === 'SUCCESS') {
                let allowClose: boolean = true;
                if ((alternateItem && (alternateItem.UseAltItemId === 'Always' || (alternateItem?.UseAltItemId === 'InventWhen0' && (!props.inventoryLabel || (props.inventoryLabel && props.inventoryLabel?.toLowerCase().search('out of stock') > -1))))) && alternateProduct) {
                    setConfirmAddedToCartDialogOpen(true);
                     updateConfirmMsg(props, props.alternateItemHeaderText!, format(props.alternateItemBodyText!, alternateProduct.productDtl?.ItemId), true);
                     allowClose = false;
                 if(props.isAlternateOversized)//(alternateProduct.productAvailibility && _getInventoryLabel(alternateProduct.productAvailibility)?.toLowerCase().search('out of stock') !== -1)
                   {
                     const cartLines = cartState && cartState.cart && cartState.cart.CartLines ? cartState.cart.CartLines.filter(x => x.ItemId === product.ItemId) : undefined;
                     if(cartLines && cartLines.length > 0)
                     {
                         const extProp: CommerceProperty[] = [];
                         const existingExtensionProp = cartState && cartState.cart && cartState.cart.ExtensionProperties;
                         cartLines.forEach(cartLine => {
                             const existingProp :CommerceProperty | undefined = existingExtensionProp && existingExtensionProp.length > 0 ? existingExtensionProp.find(ext=>ext.Key === cartLine.LineId) : undefined;
                             if(existingProp === undefined){
                                 const updatedExistingProp :CommerceProperty = {Key : cartLine.LineId, Value:{StringValue: 'Oversized Item'}};
                                 extProp.push(updatedExistingProp);
                             }
                         });
                         await cartState.updateExtensionProperties({newExtensionProperties: extProp});
                     }
                   }
                 }
                if (props.dialogStrings && addToCartBehavior === AddToCartBehavior.showModal) {
                    setDisabled(false);
                    openModal(true);
                } else if (props.dialogStrings && addToCartBehavior === AddToCartBehavior.showNotification) {
                    setDisabled(false);
                    const notification = new ItemSuccessfullyAddedToCartNotification(
                        props.context,
                        props.dialogStrings,
                        props.imageSettings,
                        props.gridSettings,
                        product,
                        data?.price,
                        quantity,
                        props.navigationUrl,
                        props.telemetryContent!,
                        props.id,
                        props.typeName
                    );
                    NotificationsManager.instance().addNotification(notification);
                } else if (MsDyn365.isBrowser && props.navigationUrl && !props.isNavigationToCartPageDisabled &&
                    (addToCartBehavior === undefined || addToCartBehavior === AddToCartBehavior.goToCart)) {
                    window.location.assign(props.navigationUrl);
                } else {
                    setDisabled(false);
                }

                if(props.isOversizedItem)
                {
                    const cartLines = cartState && cartState.cart && cartState.cart.CartLines ? cartState.cart.CartLines.filter(x => x.ItemId === product.ItemId) : undefined;
                    if(cartLines && cartLines.length>0)
                    {
                        const extProp: CommerceProperty[] = [];
                        const existingExtensionProp = cartState && cartState.cart && cartState.cart.ExtensionProperties;
                        cartLines.forEach(cartLine => {
                            const existingProp :CommerceProperty | undefined = existingExtensionProp && existingExtensionProp.length > 0 ? existingExtensionProp.find(ext=>ext.Key === cartLine.LineId) : undefined;
                            if(existingProp === undefined){
                                const updatedExistingProp :CommerceProperty = {Key : cartLine.LineId, Value:{StringValue: 'Oversized Item'}};
                                extProp.push(updatedExistingProp);
                            }
                        });
                        await cartState.updateExtensionProperties({newExtensionProperties: extProp});

                    }
                }
                propagateResult(props, addToCartResult, allowClose);
            } else {
                NotificationsManager.instance().addNotification(new ErrorNotification(
                    addToCartResult.errorDetails?.LocalizedMessage ?? 'Add to cart failed',
                    props.dialogStrings?.closeNotificationLabel ?? ''));

                propagateError(props, { failureReason: 'CARTACTIONFAILED', cartActionResult: addToCartResult });
                setDisabled(false);
            }
        // }
        // else if(isAlternateProduct && isAlternateItemOOS)
        // {
        //     updateSoftSupresedeFlag(props, true);
        //     setConfirmAddedToCartDialogOpen(true);
        //     let etaDateStr:string = '';
        //     if(alternateProduct?.etaDetails && alternateProduct?.etaDetails.ETADate && alternateProduct.etaDetails.ResultMessage)
        //     {
        //         const resultMsg: string = alternateProduct.etaDetails.ResultMessage;
        //         if(resultMsg && resultMsg.toLowerCase().search('no eta') === -1)
        //         {
        //          const etaDate = new Date(alternateProduct?.etaDetails.ETADate??'');
        //          etaDateStr = `${etaDate.toLocaleString('default', { month: 'short' }) } ${  etaDate.getDate()  }, ${  etaDate.getFullYear()}`; //etaDate.toDateString();
        //         }

        //     }
        //     updateConfirmMsg(props, props.alternateItemHeaderText!, format('The current part number has been superseded to {0}, which is not in stock. We expect to have it in stock around <b>{1}</b>. We can fulfill your order when this item becomes available. Do you want us to fulfill your order when stock is available?', alternateProduct?.productDtl?.ItemId,etaDateStr), false);
        // }

    }
    //     if(isAddtoCart){

    //  }
    //     else{
    //         setDisabled(false);
    //     }
};

const _getInventoryLabel = (productQty : IProductInventoryInformation[]|undefined)  => {
    if(productQty)
    {
        const newShippingQuantity = productQty.find(shipping => shipping.deliveryType === DeliveryMode.shipping);
        if(newShippingQuantity)
        {
            return newShippingQuantity.StockLevelLabel;
        }
    }
    return '';
};

const _addItemToCartCustom = async (props: IAddToCartComponentProps,
    setDisabled: (disabled: boolean) => void,
    openModal: (opened: boolean) => void,
    setConfirmAddedToCartDialogOpen: (opened: boolean) => void,
    currentQty: number, isItemSoftSupersedeYes?: boolean): Promise<void> => {
    //setConfirmAddedToCartDialogOpen(false);
    const addToCartBehavior = props.context.app.config.addToCartBehavior;
    const data = props.data;
    const addToCartInput = await getAddToCartInputFromProps(props);
    let product = addToCartInput[0].product;
    let availQty = props.productAvailability && props.productAvailability.AvailableQuantity;
    const quantity = addToCartInput[0].count === currentQty ? addToCartInput[0].count : currentQty;
    if (isItemSoftSupersedeYes && props.alternateProduct) {
        product = props.alternateProduct.productDtl!;
        availQty = props.alternateProduct.productAvailibility && props.alternateProduct.productAvailibility[0].ProductAvailableQuantity && props.alternateProduct.productAvailibility[0].ProductAvailableQuantity.AvailableQuantity ? props.alternateProduct.productAvailibility[0].ProductAvailableQuantity.AvailableQuantity : 0;
    }
    const cartState = await getCartState(props.context.actionContext);
    // const existqty = cartState.cart.CartLines?.filter(x => x.ItemId === product.ItemId);
    // if (existqty) {
    //     existqty.forEach(function (item, index) {
    //         qty += item.Quantity!;
    //     });

    // }

    const addToCartResult = await cartState.addProductToCart({
        product,
        count: quantity,
        availableQuantity: availQty,//props.productAvailability !== undefined && props.productAvailability.AvailableQuantity ? props.productAvailability.AvailableQuantity : undefined, //!== undefined && (props.productAvailability?.AvailableQuantity === 0 || props.productAvailability?.AvailableQuantity < quantity || props.productAvailability?.AvailableQuantity < quantity + qty) ? quantity + qty : props.productAvailability?.AvailableQuantity,
        additionalProperties: { orderQuantityLimitsFeatureIsEnabled: props.isOrderQuantityLimitsFeatureEnabled },
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- read config file.
        enableStockCheck: props.context.app.config.enableStockCheck,
        isPriceKeyedIn: props.isPriceKeyedIn,
        customPrice: props.customPriceAmount,
        isAddEmailDeliveryItemToCart: props.isAddEmailDeliveryItemToCart,
        shouldSkipSiteSettings: props.shouldSkipSiteSettings,
        catalogId: addToCartInput[0].catalogId
    });
    if (addToCartResult.status === 'SUCCESS') {
        let allowClose: boolean = true;
        if (isItemSoftSupersedeYes) {
           if(props.alternateProduct && props.alternateItem && (props.alternateItem.UseAltItemId.search('Always') === -1 || (props.alternateItem.UseAltItemId.search('Always') > -1 && (!props.alternateProduct.productAvailibility ||(props.alternateProduct.productAvailibility && _getInventoryLabel(props.alternateProduct.productAvailibility)?.toLowerCase().search('out of stock') === -1)))))
           {
            updateConfirmMsg(props, props.alternateItemHeaderText!, format(props.alternateItemBodyText!, props.alternateProduct?.productDtl?.ItemId), true);
            setConfirmAddedToCartDialogOpen(true);
           }
            allowClose = false;
        }
        else{
            setConfirmAddedToCartDialogOpen(false);
            // const cartLines = cartState && cartState.cart && cartState.cart.CartLines ? cartState.cart.CartLines.filter(x => x.ItemId === product.ItemId) : undefined;
            // if(cartLines && cartLines.length>0 && props.inventoryLabel?.toLowerCase().search('out of stock') !== -1)
            // {
            //     const extProp: CommerceProperty[] = [];
            //     const existingExtensionProp = cartState && cartState.cart && cartState.cart.ExtensionProperties;
            //     cartLines.forEach(cartLine => {
            //         const existingProp :CommerceProperty | undefined = existingExtensionProp && existingExtensionProp.length > 0 ? existingExtensionProp.find(ext=>ext.Key === cartLine.LineId) : undefined;
            //         if(existingProp === undefined){
            //             const updatedExistingProp :CommerceProperty = {Key : cartLine.LineId, Value:{StringValue: 'Backorder'}};
            //             extProp.push(updatedExistingProp);
            //         }
            //     });
            //     await cartState.updateExtensionProperties({newExtensionProperties: extProp});

            // }

        }
        if (props.dialogStrings && addToCartBehavior === AddToCartBehavior.showModal) {
            setDisabled(false);
            openModal(true);
        } else if (props.dialogStrings && addToCartBehavior === AddToCartBehavior.showNotification) {
            setDisabled(false);
            const notification = new ItemSuccessfullyAddedToCartNotification(
                props.context,
                props.dialogStrings,
                props.imageSettings,
                props.gridSettings,
                product,
                data?.price,
                quantity,
                props.navigationUrl,
                props.telemetryContent!,
                props.id,
                props.typeName
            );
            NotificationsManager.instance().addNotification(notification);
        } else if (MsDyn365.isBrowser && props.navigationUrl && !props.isNavigationToCartPageDisabled &&
            (addToCartBehavior === undefined || addToCartBehavior === AddToCartBehavior.goToCart)) {
            window.location.assign(props.navigationUrl);
        } else {
            setDisabled(false);
        }
        if(props.isOversizedItem)
        {
            const cartLines = cartState && cartState.cart && cartState.cart.CartLines ? cartState.cart.CartLines.filter(x => x.ItemId === product.ItemId) : undefined;
            if(cartLines && cartLines.length>0)
            {
                const extProp: CommerceProperty[] = [];
                const existingExtensionProp = cartState && cartState.cart && cartState.cart.ExtensionProperties;
                cartLines.forEach(cartLine => {
                    const existingProp :CommerceProperty | undefined = existingExtensionProp && existingExtensionProp.length > 0 ? existingExtensionProp.find(ext=>ext.Key === cartLine.LineId) : undefined;
                    if(existingProp === undefined){
                        const updatedExistingProp :CommerceProperty = {Key : cartLine.LineId, Value:{StringValue: 'Oversized Item'}};
                        extProp.push(updatedExistingProp);
                    }
                });
                await cartState.updateExtensionProperties({newExtensionProperties: extProp});

            }
        }
        propagateResult(props, addToCartResult, allowClose);
    } else {
        NotificationsManager.instance().addNotification(new ErrorNotification(
            addToCartResult.errorDetails?.LocalizedMessage ?? 'Add to cart failed',
            props.dialogStrings?.closeNotificationLabel ?? ''));

        propagateError(props, { failureReason: 'CARTACTIONFAILED', cartActionResult: addToCartResult,alternateItem: props.alternateItem, alternateProduct: props.alternateProduct });
        setDisabled(false);
    }
};
/**
 * Callback to handle success of adding to cart.
 * @param props - Add to cart component props.
 * @param setDisabled - Callback to update disabled state of the component.
 * @param setItemsAddedToCartDialogOpen - Callback to update disabled state of the component.
 * @param setErrorMessage - Callback to update error message state of the component.
 * @param cartState - Current cart state.
 * @param addToCartInput - Input used for adding to cart.
 * @param addToCartResult - Result of adding to cart.
 */
const handleAddItemsToCartSuccess = async (props: IAddToCartComponentProps,
    setDisabled: (disabled: boolean) => void,
    setItemsAddedToCartDialogOpen: (opened: boolean) => void,
    setErrorMessage: (message: string) => void,
    cartState: ICartState,
    addToCartInput: {
        product: SimpleProduct;
        count: number;
    }[],
    addToCartResult: ICartActionResult) => {

    // Validate cart for line errors.
    const validationResult: CartLineValidationResults = await CartsDataActions.validateForCheckoutAsync(
        { callerContext: props.context.actionContext, bypassCache: 'none' },
        cartState.cart.Id, cartState.cart.Version
    );
    const errorCount = (validationResult.ValidationFailuresByCartLines ?? []).length;
    const singleErrorCount = 1;
    const errorText = errorCount === singleErrorCount ? props.dialogStrings?.oneErrorText : props.dialogStrings?.multiErrorsText;
    const errorMessage = errorCount > zero ? format(props.dialogStrings?.validationErrorMessage ?? '', errorCount.toString(), errorText) : '';

    setErrorMessage(errorMessage);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- read config file.
    const addToCartBehavior = props.context.app.config.addToCartBehavior;

    if (props.dialogStrings && addToCartBehavior === AddToCartBehavior.showModal) {
        setDisabled(false);
        setItemsAddedToCartDialogOpen(true);
    } else if (props.dialogStrings && addToCartBehavior === AddToCartBehavior.showNotification) {
        setDisabled(false);
        const itemsAddedResource: IItemsAddedToCartResources = {
            viewCartButtonText: props.dialogStrings.goToCartText,
            closeButtonLabel: props.dialogStrings.closeNotificationLabel,
            itemsAddedToCartHeaderText: props.dialogStrings.headerMessageText,
            itemsAddedToCartFormatText: props.dialogStrings.headerItemFormatText,
            linesAddedToCartFormatText: props.dialogStrings.headerLinesFormatText ?? '{0}',
            itemsAddedValidationErrorMessage: errorMessage
        };

        const notification = new MultiItemsSuccessfullyAddedToCartNotification(
            props.context,
            itemsAddedResource,
            props.imageSettings,
            props.gridSettings,
            addToCartInput,
            props.navigationUrl,
            props.telemetryContent!,
            props.id,
            props.typeName);
        NotificationsManager.instance().addNotification(notification);
    } else if (MsDyn365.isBrowser && props.navigationUrl && !props.isNavigationToCartPageDisabled &&
        (addToCartBehavior === undefined || addToCartBehavior === AddToCartBehavior.goToCart)) {
        window.location.assign(props.navigationUrl);
    } else {
        setDisabled(false);
    }
    propagateResult(props, addToCartResult, true);
};

/**
 * Add one or multi item to cart from buy again.
 * @param props -- The props.
 * @param setDisabled -- The set disable call back.
 * @param setItemsAddedToCartDialogOpen -- The open modal call back.
 * @param setErrorMessage -- The set error message call back.
 */
const addItemsToCart = async (props: IAddToCartComponentProps,
    setDisabled: (disabled: boolean) => void,
    setItemsAddedToCartDialogOpen: (opened: boolean) => void,
    setErrorMessage: (message: string) => void): Promise<void> => {

    const addToCartInput = await getAddToCartInputFromProps(props);

    if (!ArrayExtensions.hasElements(addToCartInput)) {
        return;
    }

    const cartState = await getCartState(props.context.actionContext);
    const addToCartResult = await cartState.addProductsToCart(addToCartInput);

    if (addToCartResult.status === 'SUCCESS') {
        await handleAddItemsToCartSuccess(
            props, setDisabled, setItemsAddedToCartDialogOpen, setErrorMessage,
            cartState, addToCartInput, addToCartResult);
    } else {
        if (props.dialogStrings?.buyAgainNotificationTitle) {
            // For buy again show simple error notification.
            NotificationsManager.instance().addNotification(new ErrorNotification(
                props.dialogStrings.buyAgainNotificationTitle,
                props.dialogStrings.buyAgainNotificationCloseAriaLabel ?? ''));
        } else {
            NotificationsManager.instance().addNotification(new ErrorNotification(
                addToCartResult.errorDetails?.LocalizedMessage ?? 'Add to cart failed',
                props.dialogStrings?.closeNotificationLabel ?? ''));
        }

        propagateError(props, { failureReason: 'CARTACTIONFAILED', cartActionResult: addToCartResult });
        setDisabled(false);
    }
};

/**
 * Interface for add to cart component.
 */
export interface IAddtoCartComponent extends IComponent<IAddToCartComponentProps> {
    onClick(): (event: React.MouseEvent<HTMLElement>, props: IAddToCartComponentProps) => void;
}

/**
 * On click function.
 * @param _event - The mouse event.
 * @param props - The props.
 * @param setDisabled - Flag to define whether the element is disabled.
 * @param openModal - Flag to specify if it should open in a modal window.
 * @param setItemsAddedToCartDialogOpen - Sets items added to cart while dialog is open.
 * @param setErrorMessage - Error message.
 */
const onClick = async (_event: React.MouseEvent<HTMLElement>,
    props: IAddToCartComponentProps,
    setDisabled: (disabled: boolean) => void,
    openModal: (opened: boolean) => void,
    setItemsAddedToCartDialogOpen: (opened: boolean) => void,
    setConfirmAddedToCartDialogOpen: (opened: boolean) => void,
    setErrorMessage: (message: string) => void,
    gcBackOrderAllow?: string): Promise<void> => {
    if (!ArrayExtensions.hasElements(props.products)) {
         props.cartState = await getCartState(props.context.actionContext);
        const cartError = addToCartError(props);

        if (cartError) {
            propagateError(props, cartError);
            return;
        }

        setDisabled(true);
    }

    const hasOrderDetailsProducts = ArrayExtensions.hasElements(props.orderDetailsProducts) && props.orderDetailsProducts.length > defaultQuantity;
    const hasProducts = ArrayExtensions.hasElements(props.products) && props.products.length > defaultQuantity;

    const hasMultipleProducts = hasOrderDetailsProducts || hasProducts;

    if (props.shouldSkipSiteSettings && hasMultipleProducts) {
        await addItemsToCart(props, setDisabled, setItemsAddedToCartDialogOpen, setErrorMessage);
    } else {
        await addOneItemToCart(props, setDisabled, openModal, setConfirmAddedToCartDialogOpen, props.alternateItem, props.alternateProduct, undefined ,gcBackOrderAllow);
    }
};

/**
 * Add to cart component action constant.
 */
const AddToCartComponentActions = {
    onClick
};

/**
 * Add to cart component.
 * @param props - The props.
 * @returns - The add to cart component.
 */
export const AddToCartFunctionalComponent: React.FC<IAddToCartComponentProps> = (props: IAddToCartComponentProps) => {
    const [disabled, setDisabled] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [isItemsAddedToCartDialogOpen, setItemsAddedToCartDialogOpen] = useState(false);
    const [isConfirmAddedToCartDialogOpen, setConfirmAddedToCartDialogOpen] = useState(false);
    const [gcBackOrderAllow, setgcBackOrderAllow] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [addToCartInput, setAddToCartInput] = useState<{
        product: SimpleProduct;
        count: number;
    }[]>([]);
    const onClickHandler = async (event: React.MouseEvent<HTMLElement>) => {
        await AddToCartComponentActions.onClick(event, props, setDisabled, setModalOpen, setItemsAddedToCartDialogOpen, setConfirmAddedToCartDialogOpen, setErrorMessage, gcBackOrderAllow);
    };

    /**
     * Close dialog.
     */
    const closeItemsAddedToCartDialog = React.useCallback(() => {
        setItemsAddedToCartDialogOpen(false);
    }, []);

    const closeConfirmAddedToCartDialog = React.useCallback(() => {
        setConfirmAddedToCartDialogOpen(false);
        setDisabled(false);
    }, []);

    const closeConfirmAddedToCartWithQuickViewDialog = React.useCallback(() => {
        setDisabled(false);
        setConfirmAddedToCartDialogOpen(false);
        props.onQuickViewClosePopup(true);
        // propagateResult(props,null,true);
    }, []);

    const cofimationyesEvent = React.useCallback((qty: number) => {
        _addItemToCartCustom(props, setDisabled, setModalOpen, setConfirmAddedToCartDialogOpen, qty);
    }, []);

    const onSoftSupresedeyesEvent = React.useCallback((qty: number) => {
        setConfirmAddedToCartDialogOpen(false);
        _addItemToCartCustom(props, setDisabled, setModalOpen, setConfirmAddedToCartDialogOpen, qty, true);
        // props.onQuickViewClosePopup(true);
    }, []);

    const onSoftSupresedeNoEvent = React.useCallback((qty: number) => {
        setDisabled(false);
        setConfirmAddedToCartDialogOpen(false);
        updateSoftSupresedeFlag(props, false);
        let isHardSUpersede = false;
        if(props.alternateItem && props.alternateItem.UseAltItemId && props.alternateItem.UseAltItemId.search('Always') > -1)
        {
            isHardSUpersede = true;
        }
        if(!isHardSUpersede)
        {
            _addItemToCartCustom(props, setDisabled, setModalOpen, setConfirmAddedToCartDialogOpen, qty, false);
        }
    }, []);

    const popupPrice = props.alternateItem && props.alternateProduct &&(props.alternateItem.UseAltItemId === 'Always' || (props.alternateItem?.UseAltItemId === 'InventWhen0' && props.inventoryLabel && props.inventoryLabel.toLowerCase().search('out of stock') > -1) ) && props.alternateProduct && props.alternateProductPrice && props.alternateProductPrice.length > 0 ? props.alternateProductPrice[0] : props.data?.price;
    const popupProduct = props.alternateItem && props.alternateProduct &&(props.alternateItem.UseAltItemId === 'Always' || (props.alternateItem?.UseAltItemId === 'InventWhen0' && props.inventoryLabel && props.inventoryLabel.toLowerCase().search('out of stock') > -1) ) && props.alternateProduct ? props.alternateProduct.productDtl : props.data?.product;
    const priceComponent = popupPrice ? (
        <PriceComponent
            data={{ price: popupPrice }}
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            freePriceText={props.dialogStrings?.freePriceText}
            originalPriceText={props.dialogStrings?.originalPriceText}
            currentPriceText={props.dialogStrings?.currentPriceText}
            isPriceMinMaxEnabled={props.isPriceMinMaxEnabled}
            priceResources={{priceRangeSeparator: props.priceRangeSeparator}}
        />) : '';

    const popupProps: IPopupProps = {
        context: props.context,
        className: 'gen-add-to-cart-custom msc-add-to-cart',
        id: props.id,
        typeName: props.typeName,
        data: { product:  popupProduct, price: popupPrice },
        dialogStrings: props.dialogStrings,
        imageSettings: props.imageSettings,
        gridSettings: props.context.request.gridSettings,
        productQuantity: props.quantity !== undefined ? props.quantity : defaultQuantity,
        priceComponent,
        navigationUrl: props.navigationUrl,
        modalOpen,
        setModalOpen,
        telemetryContent: props.telemetryContent
    };

    const itemsAddedResource: IItemsAddedToCartResources = {
        viewCartButtonText: props.dialogStrings?.goToCartText ?? '',
        closeButtonLabel: props.dialogStrings?.closeNotificationLabel ?? 'Close',
        itemsAddedToCartHeaderText: props.dialogStrings?.headerMessageText ?? '',
        itemsAddedToCartFormatText: props.dialogStrings?.headerItemFormatText ?? '',
        linesAddedToCartFormatText: props.dialogStrings?.headerLinesFormatText ?? '{0}',
        itemsAddedValidationErrorMessage: errorMessage
    };

    const renderModalPopup = <Popup {...popupProps} />;

    const label = getLinkText(props);
    const payload = getPayloadObject(TelemetryEvent.AddToCart, props.telemetryContent!, label, '');
    const attributes = getTelemetryAttributes(props.telemetryContent!, payload);
    const buttonClassName = props.shouldSkipSiteSettings ? '' : 'msc-add-to-cart ';

    React.useEffect(() => {
        /**
         * Retrieves add to cart input and updates the state.
         */
        const gcBackOrdered = props.customerInfo?.ExtensionProperties?.find(f=>f.Key==="GCALLOWBACKORDERS")?.Value?.StringValue;
        setgcBackOrderAllow(gcBackOrdered ?? "-1");
        const retrieveAddToCartInput = async () => {
            const retrievedInput = await getAddToCartInputFromProps(props);
            setAddToCartInput(retrievedInput);
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Call async method as per the documentation of react useEffect.
        retrieveAddToCartInput();
    }, [props, setAddToCartInput]);
    return (
        <>
            {renderModalPopup}
            <button
                className={classnames(buttonClassName, props.className)}
                aria-label={props.addToCartArialLabel ?? label}
                title={props.addToCartArialLabel ?? label}
                {...attributes}
                onClick={onClickHandler}
                disabled={props.disabled || disabled || isIntermediateState(props) || shouldShowOutOfStock(props, false)}
            >
                {label}
            </button>
            <ItemsAddedToCartDialogComponent
                className='msc-lines-added-to-cart-dialog'
                id={props.id}
                typeName={props.typeName}
                context={props.context}
                dialogStrings={itemsAddedResource}
                data={{
                    products: addToCartInput
                }}
                isModalOpen={isItemsAddedToCartDialogOpen}
                onClose={closeItemsAddedToCartDialog}
            />
            <ConfirmAddedToCartDialog
                className='msc-confirm-added-to-cart'
                modalHeader={props.confirmStateHeaderText!}
                modalBodyText={props.confirmStateBodyText!}
                modalOpen={isConfirmAddedToCartDialogOpen}
                onClose={closeConfirmAddedToCartDialog}
                onClosePopup={closeConfirmAddedToCartWithQuickViewDialog}
                alternateItem={props.isItemSupersed}
                _addItemToCartCustom={cofimationyesEvent}
                isSoftSupresede={props.isItemSoftSupersed}
                onSoftSupresedeYesClick={onSoftSupresedeyesEvent}
                onSoftSupresedeNoClick={onSoftSupresedeNoEvent}
                isBackOrdered={props.isBackOrdered}
                isLimitedBackOrdered={props.isLimitedBackOrdered}
                quantity={props.quantity !== undefined ? props.quantity : defaultQuantity}
            />

        </>
    );
};

// Set default props
AddToCartFunctionalComponent.defaultProps = {
    quantity: defaultQuantity
};

/**
 * Resolves whether product is in stock.
 * @param props - The add to cart component props.
 * @returns Gets the link text.
 */
const getLinkText = (props: IAddToCartComponentProps): string => {
    return shouldShowOutOfStock(props, false) && props.outOfStockText ? props.outOfStockText : props.addToCartText;
};

/**
 * Resolves whether product is in stock.
 * @param props - The add to cart component props.
 * @returns The add to cart failure result.
 */
const addToCartError = (props: IAddToCartComponentProps): IAddToCartCustomFailureResult | undefined => {
    const { data, productAvailability, isCustomPriceSelected, customPriceAmount, maximumKeyInPrice, minimumKeyInPrice, defaultMaximumKeyInPrice = 100, defaultMinimumKeyInPrice = 10 } = props;
    const productId = data && data.product.ItemId;
    const cartLines = props.cartState && props.cartState.cart && props.cartState.cart.CartLines ? props.cartState.cart.CartLines.filter(x => x.ItemId === productId) : undefined;
    if (!data || !data.product.RecordId) {
        // No product exists, won't be able to add to cart
        return { failureReason: 'EMPTYINPUT' };
    }

    if (data.product.Dimensions) {
        const missingDimensions = data.product.Dimensions.filter(dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value));

        if (ArrayExtensions.hasElements(missingDimensions)) {
            // At least one dimension with no value exists on the product, won't be able to add to cart
            return { failureReason: 'MISSINGDIMENSION', missingDimensions };
        }
    }
    const isAlternateItemOOS = props.alternateItem && (props.alternateItem.UseAltItemId === 'Always' || (props.alternateItem?.UseAltItemId === 'InventWhen0' && props.inventoryLabel && props.inventoryLabel.toLowerCase().search('out of stock') > -1) ) && props.alternateProduct && props.alternateProduct.productAvailibility ? (_getInventoryLabel(props.alternateProduct.productAvailibility)!.toLowerCase().search('out of stock') > -1)? true : false  : false;
    if (shouldShowOutOfStock(props, true) || isAlternateItemOOS) {
        const defaultAvailableQuantity = 0; // Showing as out of stock if no available products found.
        let availableQuantity = props.isAlternateItemAvailable && (props.productAvailability !== undefined && props.productAvailability.AvailableQuantity !== undefined && props.productAvailability.AvailableQuantity === 0) && !isAlternateItemOOS ? props.alternateProduct && props.alternateProduct.productAvailibility && props.alternateProduct.productAvailibility[0] && props.alternateProduct.productAvailibility[0].ProductAvailableQuantity && props.alternateProduct.productAvailibility[0].ProductAvailableQuantity.AvailableQuantity !== undefined ? props.alternateProduct.productAvailibility[0].ProductAvailableQuantity.AvailableQuantity : defaultAvailableQuantity : productAvailability?.AvailableQuantity ?? defaultAvailableQuantity;
        if(props.alternateItem && isAlternateItemOOS)
        {
            availableQuantity = 0;
        }
        const stockLeft = Math.max(availableQuantity, defaultAvailableQuantity);

        return { failureReason: 'OUTOFSTOCK', stockLeft };
    }
    if(props.quantity && props.quantity > props.context.app.config.cartMaxQuantity){
        return { failureReason: 'INVALIDQUANTITY'};
    }

    if(cartLines && cartLines.length>0)
    {
        const lines = cartLines.filter(x => x.ItemId === data.product.ItemId);
        let sum: number = 0;
        if (lines !== null || lines !== undefined)
        {
            lines.forEach(a => sum += a.Quantity && a.Quantity ? a.Quantity : 0);
        }
        const cartLineQty = sum + (props.quantity && props.quantity? props.quantity:0);
    if((cartLineQty && cartLineQty  > props.context.app.config.cartMaxQuantity))
    {
        const stockLeft = sum;
        return { failureReason: 'CUSTOMQUNTITYLIMIT',stockLeft};
    }
    }
    // When Custom price is selected, if there is no keyed-in price or keyed-in price is out of limit, should return error.
    if (isCustomPriceSelected &&
        (!customPriceAmount || customPriceAmount > (maximumKeyInPrice || defaultMaximumKeyInPrice) ||
            customPriceAmount < (minimumKeyInPrice || defaultMinimumKeyInPrice))) {
        return { failureReason: 'INVALIDCUSTOMAMOUNT' };
    }

    // Only allow adding to cart if not showing out of stock
    return undefined;
};

/**
 * Resolves whether product is in stock.
 * @param props - The add to cart component props.
 * @param includeCurrentQuantity - Flag to specify whether current quantity should be included.
 * @returns The dialog element.
 */
const shouldShowOutOfStock = (props: IAddToCartComponentProps, includeCurrentQuantity: boolean): boolean => {
    if (props.context.app.config.enableStockCheck === undefined || props.context.app.config.enableStockCheck === false) {
        return false;
    }

    // When skip site settings do not need show out of stock on adding to cart
    if (props.shouldSkipSiteSettings) {
        return false;
    }

    if (
        props.isLoading ||
        props.isProductQuantityLoading ||
        props.isUpdatingDimension ||
        props.isLoadingDeliveryOptions ||
        props.isUpdatingDeliveryOptions ||
        props.isAddServiceItemToCart
    ) {
        // Out of stock turn off, don't bother showing out of stock
        return false;
    }

    if (!props.data || !props.data.product.RecordId) {
        // No product exists, don't bother showing out of stock
        return false;
    }

    const hasAvailableProducts = props.hasAvailableProducts ?? true;
    if (!hasAvailableProducts) {
        return true;
    }

    if (props.data.product.Dimensions) {
        if (props.data.product.Dimensions.find(dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value))) {
            // At least one dimension with no value exists on the product, so also don't show out of stock
            return false;
        }
    }

    const includedQuantityNumber = includeCurrentQuantity && props.quantity ? props.quantity : defaultQuantity;

    if(props.isAlternateItemAvailable)
    {
        if (props.alternateProduct && props.alternateProduct.productAvailibility && props.alternateProduct.productAvailibility[0] && props.alternateProduct.productAvailibility[0].ProductAvailableQuantity !== undefined && props.alternateProduct.productAvailibility[0].ProductAvailableQuantity.AvailableQuantity !== undefined)
        {
            if(props.alternateItem && props.alternateItem.UseAltItemId && props.alternateItem.UseAltItemId === 'Always')
            {
                if(props.alternateProduct.productAvailibility[0].ProductAvailableQuantity.AvailableQuantity >= includedQuantityNumber)
                {
                    return false;
                }
                else
                {
                   return true;
                }
            }
            else if(props.alternateItem && props.alternateItem.UseAltItemId && props.alternateItem.UseAltItemId === 'InventWhen0' && props.inventoryLabel && props.inventoryLabel && props.inventoryLabel.toLowerCase().search('out of stock') > -1)
            {
                if(props.alternateProduct.productAvailibility[0].ProductAvailableQuantity.AvailableQuantity >= includedQuantityNumber)
                {
                    return false;
                }
                else
                {
                   return true;
                }
            }
            else
            {
                return !(
                    props.productAvailability &&
                    props.productAvailability.AvailableQuantity !== undefined &&
                    props.productAvailability.AvailableQuantity >= includedQuantityNumber
                );
            }
        }
        else
        {
            return !(
                props.productAvailability &&
                props.productAvailability.AvailableQuantity !== undefined &&
                props.productAvailability.AvailableQuantity >= includedQuantityNumber
            );
        }
    }

    return !(
        props.productAvailability &&
        props.productAvailability.AvailableQuantity !== undefined &&
        props.productAvailability.AvailableQuantity >= includedQuantityNumber
    );
};

const isIntermediateState = (props: IAddToCartComponentProps): boolean => {
    if (props.data?.product.Dimensions) {
        if (props.data.product.Dimensions.find(dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value))) {
            // At least one dimension with no value exists on the product, so also not in intermediate state
            return false;
        }
    }

    if (!props.isLoading && !props.isUpdatingDimension && !props.isLoadingDeliveryOptions && !props.isUpdatingDeliveryOptions) {
        return false;
    }

    return true;
};

// @ts-expect-error
export const AddToCartComponent: React.FunctionComponent<IAddToCartComponentProps> = msdyn365Commerce.createComponent<IAddtoCartComponent>(
    'AddToCart',
    { component: AddToCartFunctionalComponent, ...AddToCartComponentActions }
);
